import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { Add, Delete, HelpOutline } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { VariableField } from "../../components/PopOutTextDialog";

interface SequenceHttpRequestFormProps {
  actionParameters: any;
  setActionParameters: (params: any) => void;
  variableFields: VariableField[];
  customVariableFields?: VariableField[];
}

export default function SequenceHttpRequestForm({
  actionParameters,
  setActionParameters,
  variableFields,
  customVariableFields = [],
}: SequenceHttpRequestFormProps) {
  // Combine standard and custom variable fields
  const allVariableFields = [...variableFields, ...customVariableFields];
  const urlRef = useRef<HTMLInputElement>(null);

  // Initialize query parameters, body, and headers as arrays if not already set
  const [queryParams, setQueryParams] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [bodyParams, setBodyParams] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [headerParams, setHeaderParams] = useState<
    Array<{ key: string; value: string }>
  >([]);

  // State for variable dropdown
  const [variableMenuAnchor, setVariableMenuAnchor] = useState<null | {
    element: HTMLElement;
    type: "query" | "body" | "header" | "url";
    index: number;
  }>(null);

  useEffect(() => {
    // Initialize all required fields with defaults
    const updates: any = {};
    let hasUpdates = false;

    if (actionParameters.url === undefined) {
      updates.url = "";
      hasUpdates = true;
    }
    if (actionParameters.method === undefined) {
      updates.method = "POST";
      hasUpdates = true;
    }
    if (actionParameters.queryParams === undefined) {
      updates.queryParams = "";
      hasUpdates = true;
    }
    if (actionParameters.body === undefined) {
      updates.body = "{}";
      hasUpdates = true;
    }
    if (actionParameters.headers === undefined) {
      updates.headers = '{"Content-Type": "application/json"}';
      hasUpdates = true;
    }

    if (hasUpdates) {
      setActionParameters({
        ...actionParameters,
        ...updates,
      });
    }

    // Initialize query params from string if needed
    if (
      actionParameters.queryParams &&
      typeof actionParameters.queryParams === "string" &&
      queryParams.length === 0
    ) {
      const params = new URLSearchParams(actionParameters.queryParams);
      const paramsArray: Array<{ key: string; value: string }> = [];
      params.forEach((value, key) => {
        paramsArray.push({ key, value });
      });
      setQueryParams(
        paramsArray.length > 0 ? paramsArray : [{ key: "", value: "" }]
      );
    } else if (queryParams.length === 0) {
      setQueryParams([{ key: "", value: "" }]);
    }

    // Initialize body params from JSON string if needed
    if (
      actionParameters.body &&
      typeof actionParameters.body === "string" &&
      bodyParams.length === 0
    ) {
      try {
        const bodyObj = JSON.parse(actionParameters.body);
        const paramsArray: Array<{ key: string; value: string }> = [];
        Object.entries(bodyObj).forEach(([key, value]) => {
          paramsArray.push({ key, value: String(value) });
        });
        setBodyParams(
          paramsArray.length > 0 ? paramsArray : [{ key: "", value: "" }]
        );
      } catch {
        setBodyParams([{ key: "", value: "" }]);
      }
    } else if (bodyParams.length === 0) {
      setBodyParams([{ key: "", value: "" }]);
    }

    // Initialize header params from JSON string if needed
    if (
      actionParameters.headers &&
      typeof actionParameters.headers === "string" &&
      headerParams.length === 0
    ) {
      try {
        const headersObj = JSON.parse(actionParameters.headers);
        const paramsArray: Array<{ key: string; value: string }> = [];
        Object.entries(headersObj).forEach(([key, value]) => {
          paramsArray.push({ key, value: String(value) });
        });
        setHeaderParams(
          paramsArray.length > 0
            ? paramsArray
            : [{ key: "Content-Type", value: "application/json" }]
        );
      } catch {
        setHeaderParams([{ key: "Content-Type", value: "application/json" }]);
      }
    } else if (headerParams.length === 0) {
      setHeaderParams([{ key: "Content-Type", value: "application/json" }]);
    }
  }, [
    actionParameters,
    queryParams.length,
    bodyParams.length,
    headerParams.length,
  ]);

  const handleUrlInputChange = (
    value: string,
    event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setActionParameters({ ...actionParameters, url: value });

    // Check for "/" trigger
    if (event && event.target.selectionStart !== null) {
      const cursorPos = event.target.selectionStart;
      if (value[cursorPos - 1] === "/") {
        // Check if this looks like a URL path separator (has :// before it)
        const beforeSlash = value.slice(0, cursorPos - 1);
        if (!beforeSlash.endsWith(":") && !beforeSlash.endsWith("/")) {
          setVariableMenuAnchor({ element: event.target, type: "url", index: 0 });
        }
      } else {
        setVariableMenuAnchor(null);
      }
    }
  };

  const insertVariable = (variable: string) => {
    if (!variableMenuAnchor) return;

    const { type, index } = variableMenuAnchor;

    if (type === "url") {
      // Remove the "/" and add the variable
      const currentValue = actionParameters.url || "";
      const newValue = currentValue.slice(0, -1) + `{{${variable}}}`;
      setActionParameters({ ...actionParameters, url: newValue });
    } else {
      let params, setParams: any, updateString: any;

      if (type === "query") {
        params = queryParams;
        setParams = setQueryParams;
        updateString = updateQueryParamsString;
      } else if (type === "body") {
        params = bodyParams;
        setParams = setBodyParams;
        updateString = updateBodyParamsString;
      } else {
        params = headerParams;
        setParams = setHeaderParams;
        updateString = updateHeaderParamsString;
      }

      const newParams = [...params];
      // Remove the "/" and add the variable
      const currentValue = newParams[index].value;
      newParams[index].value = currentValue.slice(0, -1) + `{{${variable}}}`;
      setParams(newParams);
      updateString(newParams);
    }
    setVariableMenuAnchor(null);
  };

  const addQueryParam = () => {
    setQueryParams([...queryParams, { key: "", value: "" }]);
  };

  const removeQueryParam = (index: number) => {
    const newParams = queryParams.filter((_, i) => i !== index);
    setQueryParams(
      newParams.length > 0 ? newParams : [{ key: "", value: "" }]
    );
    updateQueryParamsString(
      newParams.length > 0 ? newParams : [{ key: "", value: "" }]
    );
  };

  const updateQueryParam = (
    index: number,
    field: "key" | "value",
    value: string,
    event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newParams = [...queryParams];
    newParams[index][field] = value;
    setQueryParams(newParams);
    updateQueryParamsString(newParams);

    // Check for "/" trigger in value field
    if (field === "value" && event && event.target.selectionStart !== null) {
      const cursorPos = event.target.selectionStart;
      if (value[cursorPos - 1] === "/") {
        setVariableMenuAnchor({ element: event.target, type: "query", index });
      } else {
        setVariableMenuAnchor(null);
      }
    }
  };

  const updateQueryParamsString = (
    params: Array<{ key: string; value: string }>
  ) => {
    const validParams = params.filter((p) => p.key.trim() !== "");
    const queryString = validParams
      .map((p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
      .join("&");
    setActionParameters({ ...actionParameters, queryParams: queryString });
  };

  const addBodyParam = () => {
    setBodyParams([...bodyParams, { key: "", value: "" }]);
  };

  const removeBodyParam = (index: number) => {
    const newParams = bodyParams.filter((_, i) => i !== index);
    setBodyParams(newParams.length > 0 ? newParams : [{ key: "", value: "" }]);
    updateBodyParamsString(
      newParams.length > 0 ? newParams : [{ key: "", value: "" }]
    );
  };

  const updateBodyParam = (
    index: number,
    field: "key" | "value",
    value: string,
    event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newParams = [...bodyParams];
    newParams[index][field] = value;
    setBodyParams(newParams);
    updateBodyParamsString(newParams);

    // Check for "/" trigger in value field
    if (field === "value" && event && event.target.selectionStart !== null) {
      const cursorPos = event.target.selectionStart;
      if (value[cursorPos - 1] === "/") {
        setVariableMenuAnchor({ element: event.target, type: "body", index });
      } else {
        setVariableMenuAnchor(null);
      }
    }
  };

  const updateBodyParamsString = (
    params: Array<{ key: string; value: string }>
  ) => {
    const validParams = params.filter((p) => p.key.trim() !== "");
    const bodyObj: any = {};
    validParams.forEach((p) => {
      bodyObj[p.key] = p.value;
    });
    setActionParameters({
      ...actionParameters,
      body: JSON.stringify(bodyObj, null, 2),
    });
  };

  const addHeaderParam = () => {
    setHeaderParams([...headerParams, { key: "", value: "" }]);
  };

  const removeHeaderParam = (index: number) => {
    const newParams = headerParams.filter((_, i) => i !== index);
    setHeaderParams(
      newParams.length > 0
        ? newParams
        : [{ key: "Content-Type", value: "application/json" }]
    );
    updateHeaderParamsString(
      newParams.length > 0
        ? newParams
        : [{ key: "Content-Type", value: "application/json" }]
    );
  };

  const updateHeaderParam = (
    index: number,
    field: "key" | "value",
    value: string,
    event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newParams = [...headerParams];
    newParams[index][field] = value;
    setHeaderParams(newParams);
    updateHeaderParamsString(newParams);

    // Check for "/" trigger in value field
    if (field === "value" && event && event.target.selectionStart !== null) {
      const cursorPos = event.target.selectionStart;
      if (value[cursorPos - 1] === "/") {
        setVariableMenuAnchor({ element: event.target, type: "header", index });
      } else {
        setVariableMenuAnchor(null);
      }
    }
  };

  const updateHeaderParamsString = (
    params: Array<{ key: string; value: string }>
  ) => {
    const validParams = params.filter((p) => p.key.trim() !== "");
    const headerObj: any = {};
    validParams.forEach((p) => {
      headerObj[p.key] = p.value;
    });
    setActionParameters({
      ...actionParameters,
      headers: JSON.stringify(headerObj, null, 2),
    });
  };

  const renderVariableMenu = () => (
    <Menu
      anchorEl={variableMenuAnchor?.element}
      open={Boolean(variableMenuAnchor)}
      onClose={() => setVariableMenuAnchor(null)}
      sx={{ maxHeight: 300 }}
    >
      {allVariableFields.map((field, i) => (
        <MenuItem key={i} onClick={() => insertVariable(field.key)}>
          {field.icon && <Box sx={{ mr: 1, display: "flex" }}>{field.icon}</Box>}
          {field.label}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          HTTP Method
          <Tooltip title="GET: Retrieve data. POST: Send data to create resources. PUT/PATCH: Update existing resources. DELETE: Remove resources. Most APIs use POST for creating and GET for reading.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select the HTTP method for your request.
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Method</InputLabel>
          <Select
            label="Method"
            value={actionParameters.method || "POST"}
            onChange={(e) =>
              setActionParameters({
                ...actionParameters,
                method: e.target.value,
              })
            }
          >
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="PATCH">PATCH</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          URL
          <Tooltip title="The API endpoint to call. Use {{fieldName}} to insert member data into the URL. Example: https://api.example.com/users/{{email}}">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Enter the target URL for your HTTP request. Use {"{{fieldName}}"}{" "}
          syntax for dynamic values.
        </Typography>
        <TextField
          fullWidth
          placeholder="https://api.example.com/users/{{email}}"
          variant="outlined"
          value={actionParameters.url || ""}
          onChange={(e) => handleUrlInputChange(e.target.value, e)}
          inputRef={urlRef}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Query Parameters
          <Tooltip title="URL query string parameters (?key=value). These are appended to the URL. Use {{fieldName}} in values to insert member data dynamically.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Add query parameters as key-value pairs. Use {"{{fieldName}}"} for
          dynamic values.
        </Typography>
        {queryParams.map((param, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1,
              mb: 1,
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Parameter name"
              variant="outlined"
              size="small"
              value={param.key}
              onChange={(e) => updateQueryParam(index, "key", e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              placeholder="Value (e.g., {{email}})"
              variant="outlined"
              size="small"
              value={param.value}
              onChange={(e) =>
                updateQueryParam(index, "value", e.target.value, e)
              }
              sx={{ flex: 2 }}
            />
            <IconButton
              onClick={() => removeQueryParam(index)}
              size="small"
              disabled={queryParams.length === 1}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<Add />}
          onClick={addQueryParam}
          variant="outlined"
          size="small"
        >
          Add Parameter
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Request Body (JSON)
          <Tooltip title="Data sent in the request body (for POST/PUT/PATCH). Each key-value pair becomes a JSON field. Use {{fieldName}} in values to insert member data.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Add request body fields as key-value pairs. Use {"{{fieldName}}"} for
          dynamic values.
        </Typography>
        {bodyParams.map((param, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1,
              mb: 1,
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Field name"
              variant="outlined"
              size="small"
              value={param.key}
              onChange={(e) => updateBodyParam(index, "key", e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              placeholder="Value (e.g., {{firstName}} {{lastName}})"
              variant="outlined"
              size="small"
              value={param.value}
              onChange={(e) =>
                updateBodyParam(index, "value", e.target.value, e)
              }
              sx={{ flex: 2 }}
            />
            <IconButton
              onClick={() => removeBodyParam(index)}
              size="small"
              disabled={bodyParams.length === 1}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<Add />}
          onClick={addBodyParam}
          variant="outlined"
          size="small"
        >
          Add Field
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Headers
          <Tooltip title="HTTP headers sent with the request. Common headers: Authorization (API keys/tokens), Content-Type (usually application/json). Use {{fieldName}} for dynamic values.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Add custom headers as key-value pairs. Common headers include
          Authorization, Content-Type, etc.
        </Typography>
        {headerParams.map((param, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1,
              mb: 1,
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Header name (e.g., Authorization)"
              variant="outlined"
              size="small"
              value={param.key}
              onChange={(e) => updateHeaderParam(index, "key", e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              placeholder="Value (e.g., Bearer token)"
              variant="outlined"
              size="small"
              value={param.value}
              onChange={(e) =>
                updateHeaderParam(index, "value", e.target.value, e)
              }
              sx={{ flex: 2 }}
            />
            <IconButton
              onClick={() => removeHeaderParam(index)}
              size="small"
              disabled={headerParams.length === 1}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<Add />}
          onClick={addHeaderParam}
          variant="outlined"
          size="small"
        >
          Add Header
        </Button>
      </Box>

      <Box
        sx={{
          p: 2,
          bgcolor: "action.hover",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Type{" "}
          <Box
            component="span"
            sx={{
              bgcolor: "background.paper",
              px: 0.5,
              borderRadius: 0.5,
              fontFamily: "monospace",
            }}
          >
            /
          </Box>{" "}
          in value fields to insert member variables using {"{{variableName}}"} syntax.
        </Typography>
      </Box>

      {renderVariableMenu()}
    </Box>
  );
}
